import { BasePagination, Button, useDisclosure } from '@elkaso-app/web-design';
import { useDeleteSourceBrandLocationApi } from 'apis/crm-source-brand-locations/use-delete-source-brand-location-api';
import {
  ISourceBrandLocationData,
  useGetSourceBrandLocationsApi,
} from 'apis/crm-source-brand-locations/use-get-source-brand-locations-api';
import { useSetMasterSourceBrandLocationApi } from 'apis/crm-source-brand-locations/use-set-master-source-brand-location-api';
import { useVerifySourceBrandLocationApi } from 'apis/crm-source-brand-locations/use-verify-source-brand-location-api';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { BrandFilterButton } from '../components/filters/brand-filter';
import { CombinedLocationFilterButton } from '../components/filters/combined-location-filter';
import CreateSourceBrandLocationModal from '../components/modals/create-source-brand-location-modal';
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal';
import EditSourceBrandLocationModal from '../components/modals/edit-source-brand-location-modal';
import LocationAttributesModal from '../components/modals/location-attributes-modal';
import LocationChildrenModal from '../components/modals/location-children-modal';
import SetParentLocationModal from '../components/modals/set-parent-location-modal';
import ViewLocationDetailsModal from '../components/modals/view-location-details-modal';
import {
  ActionsCell,
  BrandInfoCell,
  LocationInfoCell,
  StatusDropdownCell,
  VerificationRadioCell,
} from '../components/table-cells';

const SourceBrandLocationsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();

  // State for search and modals
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedLocationForDelete, setSelectedLocationForDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Modal controls
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isAttributesOpen, onOpen: onAttributesOpen, onClose: onAttributesClose } = useDisclosure();
  const { isOpen: isChildrenOpen, onOpen: onChildrenOpen, onClose: onChildrenClose } = useDisclosure();
  const { isOpen: isSetParentOpen, onOpen: onSetParentOpen, onClose: onSetParentClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();

  // API hooks
  const { isLoading, isFetching, data } = useGetSourceBrandLocationsApi();
  const deleteLocationMutation = useDeleteSourceBrandLocationApi();
  const verifyLocationMutation = useVerifySourceBrandLocationApi();
  const setMasterLocationMutation = useSetMasterSourceBrandLocationApi();

  const { pageCount, total, data: locationsData } = data ?? {};

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(debouncedSearchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedSearchTerm]);

  // Filter data based on search
  const getFilteredData = () => {
    if (!locationsData) return [];

    if (debouncedSearchTerm) {
      return locationsData.filter(
        (location) =>
          location.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          location.brand.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          location.sourcePlatform.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    return locationsData;
  };

  const filteredData = getFilteredData();

  const handleViewLocation = (locationId: number) => {
    setSelectedLocationId(locationId);
    onViewOpen();
  };

  const handleEditLocation = (locationId: number) => {
    setSelectedLocationId(locationId);
    onEditOpen();
  };

  const handleDeleteLocation = (locationId: number, locationName: string) => {
    setSelectedLocationForDelete({ id: locationId, name: locationName });
    onDeleteOpen();
  };

  const handleManageAttributes = (locationId: number) => {
    setSelectedLocationId(locationId);
    onAttributesOpen();
  };

  const handleViewChildren = (locationId: number) => {
    setSelectedLocationId(locationId);
    onChildrenOpen();
  };

  const handleSetParent = (locationId: number) => {
    setSelectedLocationId(locationId);
    onSetParentOpen();
  };

  const handleVerifyLocation = async (locationId: number, isVerified: boolean) => {
    try {
      await verifyLocationMutation.mutateAsync({ id: locationId, isVerified });
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  const handleSetMasterLocation = async (locationId: number, isMaster: boolean) => {
    try {
      await setMasterLocationMutation.mutateAsync({ id: locationId, isMaster });
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  const confirmDeleteLocation = async () => {
    if (selectedLocationForDelete) {
      try {
        await deleteLocationMutation.mutateAsync(selectedLocationForDelete.id);
        onDeleteClose();
        setSelectedLocationForDelete(null);
      } catch (error) {
        // Error is handled in the API hook
      }
    }
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Source Brand Locations'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Location'
            sortButtonComponent={<BaseSortButton sortBy='name' />}
            filterButtonComponent={<CombinedLocationFilterButton />}
          />
        ),
        accessor: 'location',
        width: '35%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={LocationInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Brand' filterButtonComponent={<BrandFilterButton />} />,
        accessor: 'brand',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={BrandInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Verification' />,
        accessor: 'verification',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={VerificationRadioCell} {...value} />,
      },
      {
        Header: <TableHeader name='Status' />,
        accessor: 'status',
        width: '20%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={StatusDropdownCell} {...value} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      filteredData?.map((item: ISourceBrandLocationData) => ({
        id: item.id,
        location: {
          name: item.name,
          address: item.address,
          sourcePlatform: item.sourcePlatform,
          sourceExternalId: item.sourceExternalId,
          parentId: item.parentId,
        },
        brand: {
          name: item.brand.name,
          image: item.brand.image,
          countryCode: item.brand.countryCode,
        },
        verification: {
          locationId: item.id,
          isVerified: item.isVerified,
          onVerifyChange: handleVerifyLocation,
        },
        status: {
          locationId: item.id,
          isMaster: item.isMaster,
          onMasterChange: handleSetMasterLocation,
        },
        actions: {
          locationId: item.id,
          locationName: item.name,
          onView: handleViewLocation,
          onEdit: handleEditLocation,
          onDelete: handleDeleteLocation,
          onManageAttributes: handleManageAttributes,
          onViewChildren: handleViewChildren,
          onSetParent: handleSetParent,
        },
      })) || [],
    [filteredData]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='CRM Source Brand Locations' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <Button leftIcon={<FiPlus />} colorScheme='blue' size='sm' onClick={onCreateOpen}>
              Add Location
            </Button>
            <ClearFilterButton />
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <BaseTable columns={columns} data={tableData} />
          </div>
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {/* Create Modal */}
      <CreateSourceBrandLocationModal isOpen={isCreateOpen} onClose={onCreateClose} />

      {/* Edit Modal */}
      {selectedLocationId && (
        <EditSourceBrandLocationModal
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setSelectedLocationId(null);
          }}
          locationId={selectedLocationId}
        />
      )}

      {/* Delete Modal */}
      {selectedLocationForDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => {
            onDeleteClose();
            setSelectedLocationForDelete(null);
          }}
          onConfirm={confirmDeleteLocation}
          itemName={selectedLocationForDelete.name}
          isLoading={deleteLocationMutation.isLoading}
        />
      )}

      {/* Attributes Modal */}
      {selectedLocationId && (
        <LocationAttributesModal
          isOpen={isAttributesOpen}
          onClose={() => {
            onAttributesClose();
            setSelectedLocationId(null);
          }}
          locationId={selectedLocationId}
        />
      )}

      {/* Children Modal */}
      {selectedLocationId && (
        <LocationChildrenModal
          isOpen={isChildrenOpen}
          onClose={() => {
            onChildrenClose();
            setSelectedLocationId(null);
          }}
          locationId={selectedLocationId}
        />
      )}

      {/* Set Parent Modal */}
      {selectedLocationId && (
        <SetParentLocationModal
          isOpen={isSetParentOpen}
          onClose={() => {
            onSetParentClose();
            setSelectedLocationId(null);
          }}
          locationId={selectedLocationId}
        />
      )}

      {/* View Details Modal */}
      <ViewLocationDetailsModal
        isOpen={isViewOpen}
        onClose={() => {
          onViewClose();
          setSelectedLocationId(null);
        }}
        locationId={selectedLocationId}
      />
    </>
  );
};

export default SourceBrandLocationsListScreen;
